import {
  CONSENT_TYPES,
  CURRENT_CONSENT_DOCUMENT_VERSION,
  NIGERIAN_STATE_OPTIONS,
  type ConsentType,
  type OnboardingWizardStepKey,
} from '@solar/contracts';

export const ONBOARDING_STEP_ORDER: readonly OnboardingWizardStepKey[] = [
  'profile',
  'identity',
  'address',
  'agreements',
];

export const ONBOARDING_STEP_LABELS: Readonly<Record<OnboardingWizardStepKey, string>> = {
  profile: 'Personal details',
  identity: 'Identity verification',
  address: 'Address',
  agreements: 'Agreements',
};

export const ONBOARDING_STEP_TITLES: Readonly<Record<OnboardingWizardStepKey, string>> = {
  profile: 'Your personal details',
  identity: 'Verify your identity',
  address: 'Your address',
  agreements: 'Review your agreements',
};

export const ONBOARDING_STEP_DESCRIPTIONS: Readonly<Record<OnboardingWizardStepKey, string>> = {
  profile: 'Tell us who you are and how we can reach you about your solar account.',
  identity:
    'Upload your NIN slip so our authorized onboarding team can review your identity securely.',
  address:
    'Add your current residential address. You will choose the installation address when you apply.',
  agreements: 'Please read and accept each required agreement to finish setting up your account.',
};

export interface AgreementDefinition {
  readonly type: ConsentType;
  readonly title: string;
  readonly description: string;
}

export const REQUIRED_AGREEMENTS: readonly AgreementDefinition[] = [
  {
    type: 'PLATFORM_TERMS',
    title: 'Platform terms of use',
    description: 'Rules for using the Seamontech solar repayment portal.',
  },
  {
    type: 'PRIVACY_NOTICE',
    title: 'Privacy notice',
    description: 'How we collect, use, and protect your personal information.',
  },
  {
    type: 'REPAYMENT_AGREEMENT',
    title: 'Repayment agreement',
    description: 'Terms that apply when you finance a solar system through Seamontech.',
  },
  {
    type: 'DIRECT_DEBIT_PREPARATION',
    title: 'Direct debit preparation',
    description: 'Disclosure before a direct debit mandate is set up for repayments.',
  },
  {
    type: 'ELECTRONIC_COMMUNICATION',
    title: 'Electronic communications',
    description: 'Consent to receive contract and repayment notices electronically.',
  },
];

export { NIGERIAN_STATE_OPTIONS };

export function parseOnboardingStepParam(value: string | null): OnboardingWizardStepKey | null {
  if (
    value === 'profile' ||
    value === 'identity' ||
    value === 'address' ||
    value === 'agreements'
  ) {
    return value;
  }

  return null;
}

export function stepIndex(step: OnboardingWizardStepKey): number {
  return ONBOARDING_STEP_ORDER.indexOf(step);
}

export function nextStep(step: OnboardingWizardStepKey): OnboardingWizardStepKey | 'complete' {
  const index = stepIndex(step);

  if (index === ONBOARDING_STEP_ORDER.length - 1) {
    return 'complete';
  }

  return ONBOARDING_STEP_ORDER[index + 1] ?? 'complete';
}

export function previousStep(step: OnboardingWizardStepKey): OnboardingWizardStepKey | null {
  const index = stepIndex(step);

  if (index <= 0) {
    return null;
  }

  return ONBOARDING_STEP_ORDER[index - 1] ?? null;
}

export function isConsentCurrentVersion(documentVersion: string): boolean {
  return documentVersion === CURRENT_CONSENT_DOCUMENT_VERSION;
}

export function acceptedConsentTypes(
  consents: readonly { consentType: string; documentVersion: string }[],
): Set<string> {
  return new Set(
    consents
      .filter((consent) => isConsentCurrentVersion(consent.documentVersion))
      .map((consent) => consent.consentType),
  );
}

export function allRequiredConsentsAccepted(
  consents: readonly { consentType: string; documentVersion: string }[],
): boolean {
  const accepted = acceptedConsentTypes(consents);
  return CONSENT_TYPES.every((type) => accepted.has(type));
}
